import React from 'react';
import './AboutUs.css';

/**
 * About Us page for JG Printing.
 * Info based on client-provided details — update placeholders when client sends more.
 */

const services = [
    'Full Sublimation Uniforms',
    'Full Sublimation Polo Shirt',
    'Full Sublimation Sando',
    'Full Sublimation Muscle Tees',
    'Full Sublimation Hoodie & Hoodie Jacket',
    'Full Sublimation Jersey',
    'Full Sublimation Basketball Warmer',
    'Full Sublimation NFL',
    'Full Sublimation Oversized T-Shirt',
];

const values = [
    {
        number: '01',
        title: 'Quality First',
        description: 'Every piece that leaves our shop meets a standard we\'re proud to put our name on — vibrant colors, clean cuts, and prints that last.'
    },
    {
        number: '02',
        title: 'Affordability',
        description: 'Great printing shouldn\'t break the bank. We keep our prices competitive without cutting corners, especially on bulk orders.'
    },
    {
        number: '03',
        title: 'Customer Focus',
        description: 'From your first message to your final pickup, we make sure you\'re guided every step of the way — no guesswork, no stress.'
    },
];

function AboutUs() {
    return (
        <main className="about-page">

            {/* ── Hero ── */}
            <section className="about-hero">
                <div className="about-hero-inner">
                    <div className="about-hero-text">
                        <p className="about-eyebrow">Est. 2024</p>
                        <h1 className="about-title">
                            Quality Prints.<br />
                            <span className="about-title-accent">Affordable Prices.</span>
                        </h1>
                        <p className="about-lead">
                            JG Printing started with a simple idea — that everyone deserves
                            access to high-quality custom printing without the hefty price tag.
                            Since 2019, we've been turning ideas into wearable reality, one
                            print at a time.
                        </p>
                    </div>
                    <div className="about-hero-badge">
                        <div className="about-badge-ring">
                            <div className="about-badge-inner">
                                <span className="about-badge-year">2024</span>
                                <span className="about-badge-label">Since</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="about-hero-divider" />
            </section>

            {/* ── Story ── */}
            <section className="about-story">
                <div className="about-container about-story-grid">
                    <div className="about-story-label">
                        <span className="about-section-tag">Our Story</span>
                    </div>
                    <div className="about-story-content">
                        <h2 className="about-section-heading">From passion to print shop.</h2>
                        <p>
                            JG Printing was founded by <strong>Jason Galang</strong> — a graphic designer,
                            printerist, and entrepreneur based in Taguig City. What started as a creative
                            passion for design grew into a full-service printing business serving individuals,
                            sports teams, businesses, and everyone in between.
                        </p>
                        <p>
                            Jason built JG Printing on the belief that quality and affordability don't have
                            to be opposites. With hands-on involvement in every order and a genuine love for
                            the craft, JG Printing has earned the trust of customers across the Philippines
                            — both online and at the shop in Lower Bicutan.
                        </p>
                        {/* PLACEHOLDER: Update with more story details once client provides them */}
                        <p className="about-placeholder-note">
                            📝 <em>More details about JG Printing's journey to be added once provided by the client.</em>
                        </p>
                    </div>
                </div>
            </section>

            {/* ── Values ── */}
            <section className="about-values">
                <div className="about-container">
                    <div className="about-values-header">
                        <span className="about-section-tag">What We Stand For</span>
                        <h2 className="about-section-heading">Built on three things.</h2>
                    </div>
                    <div className="about-values-grid">
                        {values.map((v) => (
                            <div key={v.number} className="about-value-card">
                                <span className="about-value-number">{v.number}</span>
                                <h3 className="about-value-title">{v.title}</h3>
                                <p className="about-value-desc">{v.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Services ── */}
            <section className="about-services">
                <div className="about-container about-services-grid">
                    <div className="about-services-left">
                        <span className="about-section-tag about-section-tag--light">What We Offer</span>
                        <h2 className="about-section-heading about-section-heading--light">
                            Full sublimation,<br />cut &amp; sew specialists.
                        </h2>
                        <p className="about-services-desc">
                            We specialize in full sublimation printing for all kinds of sports and
                            riders wear. Big discounts available on bulk orders — perfect for teams,
                            organizations, and events.
                        </p>
                        <a
                            href="https://www.facebook.com/jgdigitalprinting"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="about-services-cta"
                        >
                            Get a Quote
                        </a>
                    </div>
                    <ul className="about-services-list">
                        {services.map((s, i) => (
                            <li key={i} className="about-service-item">
                                <span className="about-service-check">✓</span>
                                {s}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ── Owner ── */}
            <section className="about-owner">
                <div className="about-container about-owner-grid">
                    <div className="about-owner-photo-wrap">
                        <div className="about-owner-photo-placeholder">
                            <span className="about-owner-initials">JG</span>
                        </div>
                        {/* PLACEHOLDER: Replace div above with:
                            <img src={ownerPhoto} alt="Jason Galang" className="about-owner-photo" />
                            once client provides a photo */}
                    </div>
                    <div className="about-owner-info">
                        <span className="about-section-tag">The Person Behind It</span>
                        <h2 className="about-section-heading">Jason Galang</h2>
                        <p className="about-owner-role">Graphic Designer · Printerist · Entrepreneur</p>
                        <p>
                            Jason is the founder and driving force behind JG Printing. With a background
                            in graphic design and a deep understanding of the printing craft, he oversees
                            every aspect of the business — from design consultations to final quality checks.
                        </p>
                        <p>
                            His goal has always been simple: to give every customer — whether ordering
                            one piece or a hundred — the same level of quality and care.
                        </p>
                        {/* PLACEHOLDER: Add more about Jason once client provides a bio */}
                    </div>
                </div>
            </section>

            {/* ── Location ── */}
            <section className="about-location">
                <div className="about-container about-location-grid">
                    <div className="about-location-info">
                        <span className="about-section-tag">Find Us</span>
                        <h2 className="about-section-heading">Visit the Shop</h2>
                        <address className="about-address">
                            #16 Camatchile Street Purok 4<br />
                            Lower Bicutan Taguig City<br />
                            Philippines, 1632
                        </address>
                        {/* PLACEHOLDER: Add store hours once client provides them */}
                        <p className="about-placeholder-note">
                            📝 <em>Store hours to be added once provided by the client.</em>
                        </p>
                        <a
                            href="https://www.facebook.com/jgdigitalprinting"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="about-location-link"
                        >
                            Message us on Facebook →
                        </a>
                    </div>
                    <div className="about-map-wrap">
                        <iframe
                            title="JG Printing Location"
                            className="about-map"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1931.4128111531063!2d121.05988266163098!3d14.494697750036508!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397cf6a6f1a877b%3A0x920cb09e0f8dd007!2s16%20Camatchile%2C%20Lower%20Bicutan%2C%20Manila%2C%20Metro%20Manila!5e0!3m2!1sen!2sph!4v1775087530218!5m2!1sen!2sph"
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>
            </section>

        </main>
    );
}

export default AboutUs;